import { NextResponse } from 'next/server';
import type { ClubModule } from '@/lib/membersCatalogue';
import { isPaused, type ModuleVerdict } from '@/services/clubAffiliation/moduleState';
import { logModuleRefusal } from '@/lib/logModuleRefusal';

/**
 * What a club route does (Members phase 5, spec §6; every route is classified in `clubRouteIntent.ts`).
 * `intent: 'new'` starts something new (an offering, a class, a price, a staff invite…): refused while
 * the club is paused. With no intent the call manages what exists and is never refused for a pause.
 * `module` names the module the new thing belongs to (coaching), checked as well as `club_portal`.
 */
export interface ClubIntent {
  intent?: 'new';
  module?: ClubModule;
}

/** The refusal body of a new thing at a paused club (wire contract: HTTP 403). */
export interface ClubPausedBody {
  error: 'club_paused';
  module: ClubModule;
  reason: ModuleVerdict['reason'];
  until: string | null;
}

/**
 * Why something new may not start at this club, or null when it may: `club_portal` paused (lapsed,
 * ended, never affiliated, not in the tier), else `module` off. The portal is asked first, so a
 * lapsed club's refusal names the portal, the thing the club renews.
 */
export function newThingRefusal(
  modules: Record<ClubModule, Pick<ModuleVerdict, 'on' | 'reason' | 'until'>>,
  module?: ClubModule,
): ClubPausedBody | null {
  const portal = modules.club_portal;
  if (isPaused(portal)) {
    return {
      error: 'club_paused',
      module: 'club_portal',
      reason: portal.reason,
      until: portal.until,
    };
  }
  if (module && module !== 'club_portal' && !modules[module].on) {
    const a = modules[module];
    return { error: 'club_paused', module, reason: a.reason, until: a.until };
  }
  return null;
}

/**
 * The 403 for a refused new thing, and the one place such a refusal is logged (`logModuleRefusal`):
 * every TypeScript club-module refusal that answers `club_paused` is built here. `source` names the
 * club and the route or service that refused.
 */
export function clubPausedResponse(
  body: ClubPausedBody,
  source: { locationId: string; route: string },
): NextResponse {
  logModuleRefusal({
    locationId: source.locationId,
    module: body.module,
    reason: body.reason,
    route: source.route,
  });
  return NextResponse.json(body, { status: 403 });
}

const CLUB_MODULE_NAMES: readonly string[] = [
  'events',
  'ladder',
  'club_portal',
  'streaming',
  'coaching',
];

/** A `club_paused` refusal body read off the wire, or null when the body is anything else. */
export function readClubPausedBody(body: unknown): ClubPausedBody | null {
  if (typeof body !== 'object' || body === null) return null;
  const b = body as Record<string, unknown>;
  if (b.error !== 'club_paused') return null;
  if (typeof b.module !== 'string' || !CLUB_MODULE_NAMES.includes(b.module)) return null;
  if (typeof b.reason !== 'string') return null;
  return {
    error: 'club_paused',
    module: b.module as ClubModule,
    reason: b.reason as ModuleVerdict['reason'],
    until: typeof b.until === 'string' ? b.until : null,
  };
}

/** The day after a YYYY-MM-DD day (calendar arithmetic, no time zone involved). */
function nextDay(day: string): string {
  const d = new Date(`${day}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + 1);
  return d.toISOString().slice(0, 10);
}

/**
 * The enforce date to warn about, from the `club_portal` answer: before enforcement the resolver
 * answers `not_enforced` until the day before the root's enforce-from date. A club that is not
 * current or in grace by then pauses, so it is told the date (the day after `until`). Null when no
 * date is set (`until` null), or when the club is already affiliated.
 */
export function enforcementNotice(portal: {
  on: boolean;
  reason: ModuleVerdict['reason'];
  until: string | null;
  standing: string;
}): string | null {
  if (portal.reason !== 'not_enforced' || !portal.until) return null;
  if (portal.standing === 'current' || portal.standing === 'grace') return null;
  return nextDay(portal.until);
}

/**
 * The `club_paused` refusal an API error carries, or null. `apiRequest` attaches the decoded body to
 * the `ApiError` it throws, so a screen outside the club portal (the admin event form) can show the
 * reason itself; inside the portal `ClubPausedNotices` already shows it as a toast.
 */
export function clubPausedOf(err: unknown): ClubPausedBody | null {
  const carried = (err as { clubPaused?: unknown } | null | undefined)?.clubPaused;
  return readClubPausedBody(carried);
}
